import React, { useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Alert,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Modal,
  TextInput,
} from 'react-native';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import { callApi, getUserId } from '../Helper/Helper';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const IdentityScreen = ({ route }) => {
  const navigation = useNavigation();

  const [currentStep, setCurrentStep] = useState(0); // 0 = images, 1 = address + submit

  const [address, setAddress] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('United States');
  const [zipcode, setZipcode] = useState('');

  const returnToProvider = route?.params?.returnToProvider;
  const handleAlreadyVerified = () => {
    if (returnToProvider) {
      navigation.navigate('ProviderForm', { isVerified: true });
    } else {
      navigation.navigate('Home');
    }
  };

  const [selectedImages, setSelectedImages] = useState({
    front: null,
    back: null,
    selfie: null,
  });
  const [loading, setLoading] = useState(false);
  const [showSelfieOptions, setShowSelfieOptions] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);

  const takeSelfie = () => {
    launchCamera(
      {
        mediaType: 'photo',
        quality: 0.8,
        includeBase64: true,
        maxWidth: 1024,
        maxHeight: 1024,
        cameraType: 'front',
        saveToPhotos: false,
        presentationStyle: 'fullScreen',
      },
      response => {
        if (response.didCancel) return;
        if (response.errorCode) {
          console.warn('Camera error', response.errorCode, response.errorMessage);
          return;
        }
        const asset = response.assets?.[0];

        if (asset) {
          setSelectedImages(prev => ({
            ...prev,
            selfie: asset,
          }));
        }
      }
    ).then(r => {});
  };

  const selectSelfieFromGallery = async () => {
    const options = {
      mediaType: 'photo',
      quality: 0.8,
      includeBase64: true,
      maxWidth: 600,
      maxHeight: 1200,
      selectionLimit: 1,
    };

    try {
      const result = await launchImageLibrary(options);

      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Failed to select image');
        return;
      }

      const asset = result.assets?.[0];

      if (asset) {
        setSelectedImages(prev => ({
          ...prev,
          selfie: asset,
        }));
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to select image from gallery');
    } finally {
      setShowSelfieOptions(false);
    }
  };

  const pickImage = async type => {
    const options = {
      mediaType: 'photo',
      quality: 0.8,
      includeBase64: true,
      maxWidth: 1024,
      maxHeight: 1024,
    };

    if (type === 'selfie') {
      options.cameraType = 'front';
    }

    const result = await launchImageLibrary(options);

    if (result.didCancel) return;
    if (result.errorMessage) {
      Alert.alert('Error', result.errorMessage);
      return;
    }

    const asset = result.assets?.[0];
    if (asset) {
      setSelectedImages(prev => ({
        ...prev,
        [type]: asset,
      }));
    }
  };

  const allImagesSelected = selectedImages.front && selectedImages.back && selectedImages.selfie;

  const handleNext = () => {
    if (!allImagesSelected) {
      Alert.alert('Missing photos', 'Please upload the front/back of your license and a selfie.');
      return;
    }
    setCurrentStep(1);
  };

  const validateAddress = () => {
    const finalAddress = address.trim() || [street, city, state, country, zipcode].filter(p => p && p.trim()).join(', ');
    if (!street.trim()) return 'Street is required.';
    if (!city.trim()) return 'City is required.';
    if (!state.trim()) return 'State is required.';
    if (!country.trim()) return 'Country is required.';
    if (!zipcode.trim() || zipcode.trim().length < 5) return 'ZIP code is required.';
    if (!finalAddress) return 'Address is required.';
    return null;
  };

  const uploadImage = async () => {
    if (!selectedImages.front?.base64) {
      Alert.alert('Error', 'Please select front image of your license');
      return;
    }
    if (!selectedImages.back?.base64) {
      Alert.alert('Error', 'Please select back image of your license');
      return;
    }
    if (!selectedImages.selfie?.base64) {
      Alert.alert('Error', 'Please take a selfie');
      return;
    }

    const addressErr = validateAddress();
    if (addressErr) {
      Alert.alert('Validation', addressErr);
      return;
    }

    setLoading(true);
    try {
      const userId = await getUserId();
      if (!userId) {
        throw new Error('Unable to get user ID. Please try logging in again.');
      }

      const finalAddress =
        address.trim() ||
        [street, city, state, country, zipcode].filter(p => p && p.trim()).join(', ');

      const payload = {
        user_id: userId,
        address: finalAddress,
        license_front: selectedImages.front.base64,
        license_back: selectedImages.back.base64,
        selfie: selectedImages.selfie.base64,
      };

      await callApi('POST', '/identity', {
        body: payload,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      setSelectedImages({ front: null, back: null, selfie: null });
      setStreet('');
      setCity('');
      setState('');
      setCountry('United States');
      setZipcode('');
      setCurrentStep(0);

      Alert.alert(
        'Success',
        'Your Identity has been submitted. It may take upto 10 Business days to complete verification.'
      );
      navigation.navigate('Home');
    } catch (error) {
      console.error('Upload error:', error);
      Alert.alert('Error', error.message || 'Failed to upload images, Please try later');
      navigation.navigate('ProviderForm');
    } finally {
      setLoading(false);
    }
  };

  const renderSelfieOptions = () => (
    <Modal
      visible={showSelfieOptions}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowSelfieOptions(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Take Selfie</Text>

          <Pressable style={styles.modalButton} onPress={takeSelfie}>
            <Text style={styles.modalButtonIcon}>📸</Text>
            <Text style={styles.modalButtonText}>Take Photo</Text>
          </Pressable>

          <Pressable
            style={[styles.modalButton, styles.cancelButton]}
            onPress={() => setShowSelfieOptions(false)}
          >
            <Text style={styles.cancelButtonText}>Ok</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );

  const renderImageSection = type => {
    const isLicense = type === 'front' || type === 'back';
    const title = isLicense ? `Driver License ${type.charAt(0).toUpperCase() + type.slice(1)}` : 'Take a Selfie';
    const icon = isLicense ? '📸' : '🤳';

    return (
      <View style={styles.uploadArea}>
        {selectedImages[type] ? (
          <View style={styles.imagePreviewContainer}>
            <Image source={{ uri: selectedImages[type].uri }} style={styles.preview} resizeMode="contain" />
            <Pressable
              style={styles.changeImageButton}
              onPress={() => (type === 'selfie' ? setShowSelfieOptions(true) : pickImage(type))}
            >
              <Text style={styles.changeImageText}>{isLicense ? 'Change Image' : 'Retake Selfie'}</Text>
            </Pressable>
          </View>
        ) : (
          <Pressable
            style={styles.uploadButton}
            onPress={() => (type === 'selfie' ? setShowSelfieOptions(true) : pickImage(type))}
          >
            <Text style={styles.uploadIcon}>{icon}</Text>
            <Text style={styles.uploadText}>{title}</Text>
            <Text style={styles.uploadSubtext}>{isLicense ? 'Tap to browse' : 'Tap to take photo'}</Text>
          </Pressable>
        )}
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        bounces={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <Text style={styles.title}>Identity Verification</Text>

          {currentStep === 0 ? (
            <Text style={styles.subtitle}>
              Please upload your driver license and take a selfie
            </Text>
          ) : (
            <Text style={styles.subtitle}>
              Add your address details to complete submission
            </Text>
          )}
        </View>

        {currentStep === 0 && (
          <View>
            {renderImageSection('front')}
            <View style={styles.spacer} />
            {renderImageSection('back')}
            <View style={styles.spacer} />
            {renderImageSection('selfie')}

            <Pressable
              style={[
                styles.nextButton,
                (!allImagesSelected || loading) && styles.nextButtonDisabled,
              ]}
              onPress={handleNext}
              disabled={!allImagesSelected || loading}
            >
              <Text style={styles.nextButtonText}>
                {allImagesSelected ? 'Continue to Address Details' : 'Add all photos to continue'}
              </Text>
              <Text style={styles.nextButtonSubtext}>
                Step 1 of 2 · Photos
              </Text>
            </Pressable>

            <View style={styles.bottomPadding} />
            {renderSelfieOptions()}
          </View>
        )}

        {currentStep === 1 && (
          <View>
            <View style={styles.addressCard}>
              <Text style={styles.addressHint}>
                We use this to help verify identity and improve trust on the platform.
              </Text>

              <View style={styles.field}>
                <Text style={styles.label}>Street</Text>
                <TextInput
                  value={street}
                  onChangeText={setStreet}
                  placeholder="123 Main St"
                  autoCapitalize="words"
                  style={styles.input}
                />
              </View>

              <View style={styles.fieldRow}>
                <View style={[styles.field, styles.fieldHalf]}>
                  <Text style={styles.label}>City</Text>
                  <TextInput
                    value={city}
                    onChangeText={setCity}
                    placeholder="City"
                    autoCapitalize="words"
                    style={styles.input}
                  />
                </View>

                <View style={[styles.field, styles.fieldHalf]}>
                  <Text style={styles.label}>State</Text>
                  <TextInput
                    value={state}
                    onChangeText={setState}
                    placeholder="State"
                    autoCapitalize="characters"
                    style={styles.input}
                  />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={[styles.field, styles.fieldHalf]}>
                  <Text style={styles.label}>Country</Text>
                  <TextInput
                    value={country}
                    onChangeText={setCountry}
                    placeholder="Country"
                    autoCapitalize="words"
                    style={styles.input}
                  />
                </View>

                <View style={[styles.field, styles.fieldHalf]}>
                  <Text style={styles.label}>ZIP Code</Text>
                  <TextInput
                    value={zipcode}
                    onChangeText={t => setZipcode(String(t).replace(/[^\d-]/g, '').slice(0, 10))}
                    placeholder="ZIP"
                    keyboardType="numeric"
                    style={styles.input}
                    maxLength={10}
                  />
                </View>
              </View>


            </View>

            <View style={styles.stepNavRow}>
              <Pressable
                style={({ pressed }) => [
                  styles.secondaryButton,
                  pressed && !loading ? styles.buttonPressed : null,
                  loading && styles.secondaryButtonDisabled,
                ]}
                onPress={() => setCurrentStep(0)}
                disabled={loading}
              >
                <Text style={styles.secondaryButtonText}>Back</Text>
              </Pressable>

              <Pressable
                style={({ pressed }) => [
                  styles.submitButton,
                  pressed && !loading ? styles.buttonPressed : null,
                  loading && styles.submitButtonDisabled,
                ]}
                onPress={uploadImage}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Submit</Text>
                )}
              </Pressable>
            </View>

            <View style={styles.bottomPadding} />
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F7FA',
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
  },
  header: {
    paddingTop: 20,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1A2138',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
  },
  uploadArea: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  spacer: {
    height: 16,
  },
  uploadButton: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderStyle: 'dashed',
    borderRadius: 12,
    padding: 20,
    backgroundColor: '#F8FAFC',
    minHeight: 160,
  },
  uploadIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  uploadText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1A2138',
    marginBottom: 4,
  },
  uploadSubtext: {
    fontSize: 14,
    color: '#64748B',
  },
  imagePreviewContainer: {
    alignItems: 'center',
  },
  preview: {
    width: width - 80,
    height: 160,
    borderRadius: 8,
    marginBottom: 12,
  },
  changeImageButton: {
    padding: 8,
  },
  changeImageText: {
    color: '#2563EB',
    fontSize: 14,
    fontWeight: '600',
  },

  bottomPadding: {
    height: 40,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: Platform.OS === 'ios' ? 40 : 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1A2138',
    marginBottom: 20,
    textAlign: 'center',
  },
  modalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    marginBottom: 12,
  },
  modalButtonIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  modalButtonText: {
    fontSize: 16,
    color: '#1A2138',
    fontWeight: '500',
  },
  cancelButton: {
    backgroundColor: '#EFF1F5',
    marginTop: 8,
  },
  cancelButtonText: {
    color: '#DC2626',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    width: '100%',
  },
  nextButton: {
    backgroundColor: '#0F172A',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 18,
    shadowColor: '#0F172A',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
    elevation: 4,
  },
  nextButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  nextButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
    textAlign: 'center',
  },
  nextButtonSubtext: {
    marginTop: 6,
    color: 'rgba(255,255,255,0.78)',
    fontSize: 12,
    fontWeight: '700',
  },



  addressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  addressTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0F172A',
    marginBottom: 6,
  },
  addressHint: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: 18,
    marginBottom: 14,
  },
  field: {
    marginBottom: 12,
  },
  fieldRow: {
    flexDirection: 'row',
    gap: 12,
  },
  fieldHalf: {
    flex: 1,
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
    color: '#334155',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#F8FAFC',
    color: '#0F172A',
  },

  stepNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
    marginBottom: 8,
  },

  buttonPressed: {
    opacity: 0.92,
  },

  secondaryButton: {
    flex: 1,
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  secondaryButtonDisabled: {
    opacity: 0.7,
  },
  secondaryButtonText: {
    color: '#0F172A',
    fontSize: 16,
    fontWeight: '800',
  },

  submitButton: {
    flex: 1,
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2563EB',
    shadowColor: '#2563EB',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 4,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
    shadowOpacity: 0,
    elevation: 0,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
  },

});

export default IdentityScreen;
